import { useEffect, useState } from 'react'
import {
  balances as fetchBalances,
  listRates,
  me as fetchMe,
  recommend,
  type AccountBalance,
  type MeResponse,
  type Rate,
  type RecommendResponse,
} from '../lib/api'
import { Loader2 } from 'lucide-react'

export function Dashboard() {
  const [me, setMe] = useState<MeResponse | null>(null)
  const [accounts, setAccounts] = useState<AccountBalance[]>([])
  const [rates, setRates] = useState<Rate[]>([])
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([fetchMe(), fetchBalances().catch(() => ({ accounts: [] })), listRates().catch(() => ({ rates: [] }))])
      .then(([m, b, r]) => {
        setMe(m)
        setAccounts(b.accounts || [])
        setRates(r.rates || [])
      })
      .catch((e) => setErr(e?.message || 'failed to load'))
  }, [])

  if (err) return <div className="text-red-400 text-sm">{err}</div>
  if (!me) return <Spinner />

  return (
    <div className="space-y-10">
      <h1 className="text-2xl font-light text-zinc-100">overview</h1>

      <section className="grid grid-cols-4 gap-3">
        <Stat label="cards" value={me.summary.card_count} />
        <Stat label="linked accounts" value={me.summary.linked_accounts} />
        <Stat label="open SUBs" value={me.summary.open_subs} />
        <Stat label="5/24" value={me.summary.five_24_count} />
      </section>

      <section>
        <SectionHeader>balances</SectionHeader>
        {accounts.length === 0 ? (
          <Empty>no linked accounts. add one in connections.</Empty>
        ) : (
          <ul className="divide-y divide-zinc-900 border border-zinc-900 rounded">
            {accounts.map((a) => (
              <li key={a.id} className="px-4 py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="text-zinc-200">
                    {a.institution_name && (
                      <span className="text-zinc-500 mr-2">{a.institution_name}</span>
                    )}
                    {a.name}
                    {a.mask && <span className="text-zinc-600 ml-1">·{a.mask}</span>}
                  </div>
                  <div className="text-[11px] text-zinc-600">
                    {a.type}{a.subtype ? ` · ${a.subtype}` : ''}
                    {a.as_of && ` · ${new Date(a.as_of).toLocaleString()}`}
                  </div>
                </div>
                <div className="text-right num">
                  <div className="text-zinc-100">
                    {fmt(a.current)} {a.currency}
                  </div>
                  {a.available !== a.current && a.available != null && (
                    <div className="text-[11px] text-zinc-600">avail {fmt(a.available)}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <SectionHeader>quick recommend</SectionHeader>
        <Recommender />
      </section>

      <section>
        <SectionHeader>rates</SectionHeader>
        {rates.length === 0 ? (
          <Empty>no rates yet. wire bin/rates_pull.py via watchtower.</Empty>
        ) : (
          <table className="w-full text-sm border border-zinc-900 rounded overflow-hidden">
            <thead className="text-[11px] uppercase tracking-wider text-zinc-600 bg-zinc-950">
              <tr>
                <Th>source</Th>
                <Th>instrument</Th>
                <Th align="right">rate</Th>
                <Th align="right">as of</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {rates.map((r) => (
                <tr key={`${r.source}-${r.instrument}`}>
                  <Td>{r.source}</Td>
                  <Td>{r.instrument}</Td>
                  <Td align="right" className="num">{(+r.rate).toFixed(2)}%</Td>
                  <Td align="right" className="text-zinc-500">{r.as_of_date}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}

function Recommender() {
  const [amount, setAmount] = useState('120')
  const [category, setCategory] = useState('groceries')
  const [result, setResult] = useState<RecommendResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setErr(null)
    try {
      const r = await recommend(parseFloat(amount), category)
      setResult(r)
    } catch (e: any) {
      setErr(e?.message || 'failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <form onSubmit={submit} className="flex items-center gap-2 mb-4">
        <span className="text-zinc-600 text-sm">$</span>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          className="w-24 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-100 num focus:outline-none focus:border-zinc-600"
        />
        <input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="category"
          className="flex-1 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-zinc-600"
        />
        <button
          type="submit"
          disabled={loading}
          className="px-3 py-1.5 rounded bg-zinc-100 text-zinc-900 text-sm font-medium hover:bg-white disabled:opacity-40"
        >
          {loading ? '…' : 'rank'}
        </button>
      </form>
      {err && <div className="text-red-400 text-sm">{err}</div>}
      {result && result.ranked.length === 0 && (
        <Empty>no active cards. add some in /cards.</Empty>
      )}
      {result && result.ranked.length > 0 && (
        <ol className="space-y-2">
          {result.ranked.map((r, idx) => (
            <li
              key={r.card_id}
              className={
                'flex items-center justify-between border rounded px-4 py-3 ' +
                (idx === 0
                  ? 'border-emerald-700/50 bg-emerald-950/20'
                  : 'border-zinc-900')
              }
            >
              <div>
                <div className="text-zinc-200 text-sm">
                  {idx === 0 && <span className="text-emerald-500 mr-2">▸</span>}
                  {r.name}
                </div>
                <div className="text-[11px] text-zinc-500">{r.reasoning}</div>
              </div>
              <div className="text-right num">
                <div className="text-zinc-100 text-sm">${r.total_value.toFixed(2)}</div>
                {r.sub_dollars > 0 && (
                  <div className="text-[10px] text-emerald-500">+${r.sub_dollars.toFixed(2)} SUB</div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="border border-zinc-900 rounded px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-zinc-600">{label}</div>
      <div className="text-2xl text-zinc-100 num mt-1">{value}</div>
    </div>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">{children}</h2>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="border border-dashed border-zinc-900 rounded px-4 py-6 text-center text-zinc-600 text-sm">
      {children}
    </div>
  )
}

function Spinner() {
  return (
    <div className="flex items-center gap-2 text-zinc-600 text-sm">
      <Loader2 className="animate-spin" size={14} /> loading…
    </div>
  )
}

function fmt(n: number | null): string {
  if (n == null) return '—'
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th className={'px-3 py-2 font-normal ' + (align === 'right' ? 'text-right' : 'text-left')}>
      {children}
    </th>
  )
}
function Td({
  children,
  align,
  className,
}: {
  children: React.ReactNode
  align?: 'right'
  className?: string
}) {
  return (
    <td
      className={
        'px-3 py-2 ' + (align === 'right' ? 'text-right ' : '') + (className || '')
      }
    >
      {children}
    </td>
  )
}
